import type { GameInfoType } from '@/pages/dashboard/components/Navigation/GameSelector/types';

/** buildDashboardStyleGameLists 返回值 */
export interface IBuildDashboardStyleGameListsResult {
  /** 经分同款主版本列表（含过滤后的子版本） */
  gameList: GameInfoType[];
  /** 最近浏览主版本列表（去重、保持接口顺序） */
  recentList: GameInfoType[];
}

/** 最近浏览接口原始项 */
interface IRecentGameRaw {
  game_code?: string;
}

/**
 * 过滤无权限游戏（ChartPopover 仅展示可选游戏，不做经分「无权限分区」展示）。
 * - 子版本：仅保留 is_access
 * - 主版本：自身有权限，或仍有可访问子版本
 */
export const filterAccessibleMainGames = (mainGames: any[]): any[] => {
  const withAccessibleSubs = (mainGames || []).map((item) => ({
    ...item,
    game_List: (item?.game_List || []).filter((subItem: any) => subItem?.is_access),
  }));
  return withAccessibleSubs.filter(
    (item) => item?.is_access || (item?.game_List?.length > 0),
  );
};

/** 扁平化为可选叶子游戏（默认选中、listMenu 请求共用） */
export const flattenAccessibleLeafGames = (mainGames: any[]): any[] => (
  filterAccessibleMainGames(mainGames).flatMap((g) => {
    const accessSubs = g.game_List || [];
    if (accessSubs.length > 0) return accessSubs;
    return g.is_access ? [g] : [];
  })
);

/**
 * 按界面语言映射主版本展示名（与经分 GameSelector 一致）
 */
const mapGameLocale = (game: any, isEN: boolean) => ({
  ...game,
  game_name: isEN
    ? (game.game_name || game.game_cn_name)
    : (game.game_cn_name || game.game_name),
  game_cn_name: isEN ? game.game_cn_name : game.game_name,
});

/**
 * 将 userMessage main_game + recent 接口数据转为 ChartPopover 可选游戏列表
 * @param mainGames - 主版本游戏列表（含 game_List）
 * @param recentListRaw - 最近浏览原始记录（仅含 game_code）
 * @param locale - 当前界面语言（如 en-US / zh-CN）
 */
export const buildDashboardStyleGameLists = (
  mainGames: any[],
  recentListRaw: IRecentGameRaw[],
  locale: string,
): IBuildDashboardStyleGameListsResult => {
  const isEN = locale === 'en-US';
  const gameList = filterAccessibleMainGames(mainGames).map((hostGame) =>
    mapGameLocale(hostGame, isEN),
  );

  const recentList = (recentListRaw || [])
    .map((recentGame) => {
      const recentCode = recentGame?.game_code;
      if (!recentCode) return null;
      return gameList.find((game) => (
        game?.game_List?.some((item: any) => item.game_code === recentCode)
        || (game.game_code === recentCode && game.is_access)
      )) || null;
    })
    .filter(Boolean) as GameInfoType[];

  const seen = new Set<string>();
  const uniqueRecentList = recentList.filter((game) => {
    if (seen.has(game.game_code)) return false;
    seen.add(game.game_code);
    return true;
  });

  return {
    gameList,
    recentList: uniqueRecentList,
  };
};
